import React, { useState, useContext } from "react";
import {
  Button,
  TextField,
  Card,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  OutlinedInput,
  CardHeader,
  CardContent,
  Typography,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { registerAccount } from "../API/UsersApi";
import { InputAdornment, IconButton } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Prefixes, UserRoles } from "../Utility/Constants";
import { useTheme } from "@mui/material/styles";
import { PhotoCamera, Delete } from "@mui/icons-material";
import AuthContext from "../Context/AuthProvider";
import { Checkbox, ListItemText } from "@mui/material";
import { useSnackbar } from "../Utility/UserMessageContext";
import { getDisplayValue } from "../Utility/Utility";
import SimpleTextField from "../CustomComponents/ChpTextField";
import MenuBar from "../common/MenuBar";
import "../common/styles/CommonStyles.css"; // Import your custom styles
import GenericTextField from "../common/GenericTextField";
import HeaderValueDisplay from "../common/HeaderValueDisplay";
import GenericDatePicker from "../common/GenericDatePicker";
import GenericDropDown from "../common/GenericDropDown";
import GenericFileUploader from "../common/GenericFileUploader";

function NewUser({ tempId, onNewUserAdded, facilityOptions = [] }) {
  const [userDetails, setUserDetails] = useState({
    salutation: "",
    emailId: "",
    password: "",
    confirmPassword: "",
    firstName: "",
    lastName: "",
    designation: "",
    suffix: "",
    professionalId: "",
    mobileNo: "",
    roles: [],
  });
  const theme = useTheme();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordMismatch, setPasswordMismatch] = useState(false);
  const { auth } = useContext(AuthContext);
  const { showSnackbar } = useSnackbar();

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  };

  function getStyles(option, selectedOptions, theme) {
    return {
      fontWeight: selectedOptions.includes(option)
        ? theme.typography.fontWeightMedium
        : theme.typography.fontWeightRegular,
    };
  }

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Specific validations on change
    if (name === "mobileNo") {
      // Allow only digits and limit to 10 characters
      const digitsOnly = value.replace(/\D/g, "").slice(0, 10);
      setUserDetails((prev) => ({
        ...prev,
        [name]: digitsOnly,
      }));
      return;
    }

    // Apply the same validation to firstName, lastName and suffix
    if (name === "suffix" || name === "firstName" || name === "lastName") {
      // Allow only alphabets, underscore, hyphen and spaces
      const validatedValue = value.replace(/[^a-zA-Z_-\s]/g, "").slice(0, 100);
      setUserDetails((prev) => ({
        ...prev,
        [name]: validatedValue,
      }));
      return;
    }

    setUserDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Special handler for password fields to check for mismatch
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;

    setUserDetails((prev) => {
      const updatedDetails = {
        ...prev,
        [name]: value,
      };

      // Check for password mismatch
      if (
        (name === "password" && updatedDetails.confirmPassword) ||
        (name === "confirmPassword" && updatedDetails.password)
      ) {
        setPasswordMismatch(
          updatedDetails.password !== updatedDetails.confirmPassword
        );
      }

      return updatedDetails;
    });
  };

  const handleRolesChange = (event) => {
    const { value } = event.target;
    setUserDetails((prev) => ({
      ...prev,
      roles: typeof value === "string" ? value.split(",") : value,
    }));
  };

  const validatePasswordStrength = (password) => {
    if (!password || password.length < 12) {
      return "Password must be at least 12 characters long";
    }

    let characterTypesCount = 0;
    if (/[A-Z]/.test(password)) characterTypesCount++; // Uppercase
    if (/[a-z]/.test(password)) characterTypesCount++; // Lowercase
    if (/[0-9]/.test(password)) characterTypesCount++; // Numbers
    if (/[^A-Za-z0-9]/.test(password)) characterTypesCount++; // Special characters

    if (characterTypesCount < 4) {
      return "Password must include all of: uppercase letters, lowercase letters, numbers, and special characters";
    }

    return null;
  };

  const validateForm = () => {
    // Check if all mandatory fields are empty
    const emptyFields = [];
    const requiredFields = [
      "firstName",
      "lastName",
      "emailId",
      "password",
      "confirmPassword",
      "mobileNo",
    ];

    requiredFields.forEach((field) => {
      if (!userDetails[field]) emptyFields.push(field);
    });

    if (userDetails.roles.length === 0) emptyFields.push("roles");

    // If all required fields are empty, show a default message
    if (emptyFields.length === requiredFields.length + 1) {
      // +1 for roles
      showSnackbar("All mandatory fields are required", "error");
      return false;
    }

    // Required fields validation
    if (!userDetails.firstName) {
      showSnackbar("First name is required", "error");
      return false;
    }

    if (!userDetails.lastName) {
      showSnackbar("Last name is required", "error");
      return false;
    }

    if (!userDetails.emailId) {
      showSnackbar("Email is required", "error");
      return false;
    }

    // Basic email validation
    if (userDetails.emailId && !/\S+@\S+\.\S+/.test(userDetails.emailId)) {
      showSnackbar("Email address is invalid", "error");
      return false;
    }

    if (!userDetails.password) {
      showSnackbar("Password is required", "error");
      return false;
    }

    if (!userDetails.confirmPassword) {
      showSnackbar("Confirm password is required", "error");
      return false;
    }

    if (userDetails.roles.length === 0) {
      showSnackbar("At least one role is required", "error");
      return false;
    }

    if (!userDetails.mobileNo) {
      showSnackbar("Mobile number is required", "error");
      return false;
    }

    if (userDetails.mobileNo.length !== 10) {
      showSnackbar("Mobile number must be exactly 10 digits", "error");
      return false;
    }

    // Password policy validation
    const passwordError = validatePasswordStrength(userDetails.password);
    if (passwordError) {
      showSnackbar(passwordError, "error");
      return false;
    }

    // Password match validation
    if (userDetails.password !== userDetails.confirmPassword) {
      setPasswordMismatch(true);
      showSnackbar("Passwords do not match", "error");
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    setIsSubmitted(true);

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Remove confirmPassword field before sending to API
      const userToRegister = { ...userDetails };
      userToRegister.facilityId = auth.selectedFacility.id;
      delete userToRegister.confirmPassword;

      const result = await registerAccount(auth, userToRegister);

      if (result.isSuccess) {
        showSnackbar("User registered successfully", "success");
        // Reset form
        setUserDetails({
          salutation: "",
          emailId: "",
          password: "",
          confirmPassword: "",
          firstName: "",
          lastName: "",
          designation: "",
          suffix: "",
          professionalId: "",
          mobileNo: "",
          roles: [],
          photo: "",
        });
        setIsSubmitted(false);

        // Call the parent component's callback function to refresh the user list
        if (onNewUserAdded) {
          onNewUserAdded();
        }
      } else {
        // More comprehensive check for duplicate email errors
        const errorMessage =
          result.data?.message ||
          result.message ||
          JSON.stringify(result.data) ||
          JSON.stringify(result);

        console.log("Error response:", errorMessage);

        if (
          errorMessage.toLowerCase().includes("email") ||
          errorMessage.toLowerCase().includes("duplicate") ||
          errorMessage.toLowerCase().includes("already exists") ||
          errorMessage.toLowerCase().includes("already in use")
        ) {
          showSnackbar("Email address already exists", "error");
        } else {
          showSnackbar(
            "Registration failed: " +
              (result.data?.message || "Unknown error occurred"),
            "error"
          );
        }
      }
    } catch (error) {
      console.error("Error registering user:", error);

      // Extract error message from all possible locations
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data ||
        error?.message ||
        JSON.stringify(error?.response?.data) ||
        JSON.stringify(error);

      console.log("Caught error:", errorMessage);

      if (
        typeof errorMessage === "string" &&
        (errorMessage.toLowerCase().includes("email") ||
          errorMessage.toLowerCase().includes("duplicate") ||
          errorMessage.toLowerCase().includes("already exists") ||
          errorMessage.toLowerCase().includes("already in use"))
      ) {
        showSnackbar("Email address already exists", "error");
      } else {
        showSnackbar("Failed to register user. Please try again.", "error");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handlePhotoUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const fileSizeLimit = 1 * 1024 * 1024; // 1MB
    const allowedTypes = ["image/jpeg", "image/png", "image/gif"];

    if (!allowedTypes.includes(file.type)) {
      showSnackbar("Only JPG, PNG, and GIF files are allowed.", "error");
      return;
    }

    if (file.size > fileSizeLimit) {
      showSnackbar("File size must be less than 1MB.", "error");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setUserDetails((prev) => ({
        ...prev,
        photo: e.target.result,
      }));
    };
    reader.readAsDataURL(file);
  };

  const handlePhotoDelete = () => {
    setUserDetails((prev) => ({
      ...prev,
      photo: "",
    }));
  };

  return (
    <Grid2 container size={12} spacing={1}>
     {/* Submit Action */}
<Grid2 container size={12} >
  <Grid2 size={12}>
    <MenuBar
      showSave={true}
      onSave={() => handleSubmit()}
    />
  </Grid2>
</Grid2>
      <Grid2
        container
        size={12}
        spacing={1}
        sx={{ ml: "5px", maxHeight: "calc(100vh - 150px)", overflowY: "auto" }}
      >
        <Grid2
          container
          size={{ xs: 12, sm: 12, md: 7, lg: 7, xl: 7 }}
          spacing={1}
          sx={{ ml: "5px" }}
        >
          <Card
            className="outerCard"
            variant="outlined"
            sx={{ padding: "5px", marginTop: "5px", width: "100%" }}
          >
            <Grid2 container size={12} spacing={3}>
              <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                <GenericDropDown
                  label="Prefix"
                  value={userDetails.salutation || ""}
                  onChange={(e) => handleChange(e)}
                  options={Prefixes}
                  required
                   InputLabelProps={{
                          sx: {
                            top: "-7px",
                          },
                        }}
                  error={isSubmitted && !userDetails.salutation}
                  name="salutation"
                  size="small"
                  sx={{
                    "& .MuiFormLabel-asterisk": {
                      color: "red",
                    },
                  }}
                />
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                <GenericTextField
                  label="Suffix"
                  variant="outlined"
                  fullWidth
                  size="small"
                  name="suffix"
                  value={userDetails.suffix}
                  onChange={handleChange}
                  helperText={"Allowed: A-Z, 0-9, _, -. Max 100 chars."}
                  FormHelperTextProps={{
                    sx: {
                      fontSize: "0.7rem",
                    },
                  }}
                />
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                <GenericTextField
                  label="First Name"
                  name="firstName"
                  value={userDetails.firstName}
                  size="small"
                  onChange={handleChange}
                  error={isSubmitted && !userDetails.firstName}
                  required={true}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                <GenericTextField
                  label="Last Name"
                  name="lastName"
                  size="small"
                  value={userDetails.lastName}
                  onChange={handleChange}
                  error={isSubmitted && !userDetails.lastName}
                  required={true}
                />
              </Grid2>

              <Grid2
                size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
                backgroundColor="white"
              >
                <TextField
                  label="Email ID"
                  variant="outlined"
                  fullWidth
                  size="small"
                  name="emailId"
                  value={userDetails.emailId}
                  onChange={handleChange}
                  error={isSubmitted && !userDetails.emailId}
                  required
                  sx={{
                    "& .MuiFormLabel-asterisk": {
                      color: "red",
                    },
                  }}
                />
              </Grid2>

              <Grid2
                size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
                backgroundColor="white"
              >
                <TextField
                  label="Mobile Number"
                  variant="outlined"
                  fullWidth
                  size="small"
                  name="mobileNo"
                  value={userDetails.mobileNo}
                  onChange={handleChange}
                  error={isSubmitted && !userDetails.mobileNo}
                  required
                  sx={{
                    "& .MuiFormLabel-asterisk": {
                      color: "red",
                    },
                  }}
                  helperText="Allowed: 0-9. Max 10 digits."
                  FormHelperTextProps={{
                    sx: {
                      fontSize: "0.7rem",
                    },
                  }}
                />
              </Grid2>
              <Grid2
                size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
                backgroundColor="white"
              >
                <TextField
                  label="Password"
                  variant="outlined"
                  fullWidth
                  size="small"
                  name="password"
                  onPaste={(e) => e.preventDefault()}
                  onCopy={(e) => e.preventDefault()}
                  type={showPassword ? "text" : "password"}
                  value={userDetails.password}
                  onChange={handlePasswordChange}
                  error={
                    isSubmitted && (!userDetails.password || passwordMismatch)
                  }
                  required
                  helperText={
                    passwordMismatch &&
                    userDetails.password &&
                    userDetails.confirmPassword
                      ? "Passwords do not match"
                      : "Password must have 12+ chars, incl. A-Z, a-z, 0-9, symbol"
                  }
                  FormHelperTextProps={{
                    sx: {
                      fontSize: "0.7rem",
                      color: passwordMismatch ? "error.main" : "text.secondary",
                    },
                  }}
                  sx={{
                    "& .MuiFormLabel-asterisk": {
                      color: "red",
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid2>

              <Grid2
                size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
                backgroundColor="white"
              >
                <TextField
                  label="Confirm Password"
                  variant="outlined"
                  fullWidth
                  size="small"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={userDetails.confirmPassword}
                  onChange={handlePasswordChange}
                  onPaste={(e) => e.preventDefault()} // Prevent paste
                  onCopy={(e) => e.preventDefault()}
                  error={
                    isSubmitted &&
                    (!userDetails.confirmPassword || passwordMismatch)
                  }
                  required
                  helperText={
                    passwordMismatch &&
                    userDetails.password &&
                    userDetails.confirmPassword
                      ? "Passwords do not match"
                      : ""
                  }
                  FormHelperTextProps={{
                    sx: {
                      fontSize: "0.7rem",
                      color: "error.main",
                    },
                  }}
                  sx={{
                    "& .MuiFormLabel-asterisk": {
                      color: "red",
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle confirm password visibility"
                          onClick={handleClickShowConfirmPassword}
                          edge="end"
                        >
                          {showConfirmPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                <GenericDropDown
                  label="Roles"
                  value={userDetails.roles}
                  onChange={(e) => handleRolesChange(e)}
                  options={UserRoles}
                  required
                  error={isSubmitted && userDetails.roles.length === 0}
                  name="roles"
                  size="small"
                  multiple
                  renderValue={(selected) =>
                    selected.map(getDisplayValue).join(", ")
                  }
                  showCheckbox={true}
                  getDisplayValue={getDisplayValue}
                  sx={{
                    "& .MuiFormLabel-asterisk": {
                      color: "red",
                    },
                  }}
                />
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                <GenericTextField
                  label="Designation"
                  variant="outlined"
                  fullWidth
                  size="small"
                  name="designation"
                  value={userDetails.designation}
                  onChange={handleChange}
                />
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                <GenericTextField
                  label="Professional ID"
                  variant="outlined"
                  fullWidth
                  size="small"
                  name="professionalId"
                  value={userDetails.professionalId}
                  onChange={handleChange}
                />
              </Grid2>
            </Grid2>
          </Card>
        </Grid2>
        <Grid2
          size={{ xs: 6, sm: 6, md: 6, lg: 2, xl: 2 }}
          sx={{ mt: "3px" }}
          container
          alignItems="flex-start"
          justifyContent="flex-start"
          direction="column"
        >
          <Card variant="outlined" sx={{ padding: "10px", width: "100%" }}>
            <CardHeader subheader="Upload Image" sx={{ paddingX: 0 }} />
            <CardContent>
              <Grid2
                container
                alignItems="center"
                justifyContent="center"
                spacing={2}
              >
                <Grid2 size={12} container justifyContent="center">
                  <img
                    src={
                      userDetails.photo ||
                      "./placeholder-image-male-gender.webp"
                    }
                    alt="PatientPhoto"
                    width="120"
                    height="120"
                    className="border rounded p-0"
                  />
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ padding: 0 }}
                  >
                    Upload image size max 1MB
                  </Typography>
                </Grid2>

                {/* File Input (Hidden) */}
                <input
                  type="file"
                  accept="image/jpeg, image/png, image/gif"
                  hidden
                  id="photo-upload"
                  onChange={handlePhotoUpload}
                />

                <Grid2
                  size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
                  container
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                  spacing={1}
                >
                  {/* Upload Button */}
                  <Grid2>
                    <label htmlFor="photo-upload">
                      <IconButton component="span">
                        <PhotoCamera />
                      </IconButton>
                    </label>
                  </Grid2>

                  {userDetails.photo && (
                    <Grid2>
                      <IconButton onClick={handlePhotoDelete}>
                        <Delete />
                      </IconButton>
                    </Grid2>
                  )}
                </Grid2>
              </Grid2>
            </CardContent>
          </Card>
        </Grid2>
      </Grid2>
    </Grid2>
  );
}

export default NewUser;
